import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleOutlined,
  WarningFilled
} from "@ant-design/icons"
import { Card, Flex, Form, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import useDebounce from "../../../hooks/use-debounce.ts"
import {
  calculateContractValidation,
  calculateMilestoneTimelineVsProjectDuration
} from "./calculate-contract-validation.ts"
import {
  formatMilestoneTimelineLine,
  formatSalesPriceLine,
  type ValidationLine
} from "./format-contract-validation.ts"
import type { ContractValidationFormValues } from "./types.ts"

const { Text } = Typography

type NonValidStatus = "warning" | "error" | "incomplete"

interface ContractValidationCardProps<T extends ContractValidationFormValues> {
  form: FormInstance<T>
  className?: string
}

/**
 * "Contract Validation" card — a guardrail, not a validator: it never
 * disables Save/Generate, and recalculates live (debounced) from the form's
 * own current values as the user edits Sales Price, Project Duration, any
 * milestone price/duration/dates, or the Start/End Date, entirely
 * client-side (see `calculateContractValidation`). Renders exactly two
 * rows — Sales Price vs. Milestone Total, and Milestone duration/timeline
 * vs. Project duration (the project's own timeline vs. the earliest
 * milestone Target Start through the latest Target End, see
 * `calculateMilestoneTimelineVsProjectDuration`) — each shown only when it
 * is NOT `valid`; an all-clear line when both are `valid`. Only rendered
 * for the latest/editable Contract version —
 * enforced by the caller (the parent page already refuses to render the
 * editable form at all for a historical version), not by a prop on this
 * component.
 */
function ContractValidationCard<T extends ContractValidationFormValues>({
  form,
  className
}: ContractValidationCardProps<T>) {
  const { t } = useTranslation()

  // Six scoped watches — not one selector over the whole values object — so
  // an edit anywhere else in this (potentially large) Contract form never
  // restarts this card's debounce timer. `Form.useWatch`'s SELECTOR overload
  // returns `undefined` until the store's first change event after mount and
  // never reflects `initialValues` on the very first render (the same quirk
  // `TemplateDocumentForm`'s own `blocks` watch works around); the plain
  // NamePath overload used here reads the live value directly instead, so
  // no such fallback is needed.
  // `T` is generic (so callers can pass their own concrete
  // `FormInstance<TemplateDocumentFormData>` etc. without a cast at the call
  // site), which makes antd's own `NamePath<T>` typing for `useWatch`/
  // `getFieldValue` too strict to resolve a plain string literal — narrowed
  // to an untyped form handle here, internally only, for these six reads.
  const untypedForm = form as unknown as FormInstance<Record<string, unknown>>
  const salesPrice = Form.useWatch(["sales_price"], untypedForm)
  const duration = Form.useWatch(["duration"], untypedForm)
  const durationType = Form.useWatch(["duration_type"], untypedForm)
  const startDate = Form.useWatch(["start_date"], untypedForm)
  const completionDate = Form.useWatch(["completion_date"], untypedForm)
  const phases = Form.useWatch(["phases"], untypedForm)

  const watched: ContractValidationFormValues = {
    sales_price: salesPrice ?? untypedForm.getFieldValue("sales_price"),
    duration: duration ?? untypedForm.getFieldValue("duration"),
    duration_type: durationType ?? untypedForm.getFieldValue("duration_type"),
    start_date: startDate ?? untypedForm.getFieldValue("start_date"),
    completion_date: completionDate ?? untypedForm.getFieldValue("completion_date"),
    phases: (phases ?? untypedForm.getFieldValue("phases")) as
      | ContractValidationFormValues["phases"]
  }

  const debounced = useDebounce(watched, 400)

  const result = useMemo(() => calculateContractValidation(debounced), [debounced])
  const milestoneTimeline = useMemo(
    () => calculateMilestoneTimelineVsProjectDuration(debounced),
    [debounced]
  )

  const lines = useMemo(() => {
    const collected: Array<ValidationLine & { status: NonValidStatus }> = []

    const salesPriceLine = formatSalesPriceLine(t, result.sales_price)
    if (salesPriceLine && result.sales_price.status !== "valid") {
      collected.push({ ...salesPriceLine, status: result.sales_price.status as NonValidStatus })
    }

    const milestoneTimelineLine = formatMilestoneTimelineLine(t, milestoneTimeline)
    if (milestoneTimelineLine) {
      collected.push({ ...milestoneTimelineLine, status: "warning" })
    }

    return collected
  }, [result, milestoneTimeline, t])

  const iconFor = (status: NonValidStatus) => {
    if (status === "warning") {
      return <WarningFilled style={{ color: "#faad14", marginTop: 2 }} />
    }
    if (status === "error") {
      return <CloseCircleFilled style={{ color: "#ff4d4f", marginTop: 2 }} />
    }
    return <MinusCircleOutlined style={{ color: "rgba(0, 0, 0, 0.45)", marginTop: 2 }} />
  }

  return (
    <Card
      size={"small"}
      className={className}
      title={t("contractValidation.cardTitle")}
      data-testid={"contract-validation-card"}
    >
      {lines.length === 0 ? (
        <Flex
          align={"center"}
          gap={8}
          data-testid={"contract-validation-all-clear"}
        >
          <CheckCircleFilled style={{ color: "#52c41a" }} />
          <Text>{t("contractValidation.allClear")}</Text>
        </Flex>
      ) : (
        <Flex vertical gap={10}>
          {lines.map((line, index) => (
            <Flex
              key={index}
              gap={8}
              align={"flex-start"}
              data-testid={`contract-validation-line-${line.status}`}
            >
              {iconFor(line.status)}
              <Flex vertical>
                <Text>{line.title}</Text>
                {line.comparison &&
                  (Array.isArray(line.comparison) ? line.comparison : [line.comparison]).map(
                    (comparison, comparisonIndex) => (
                      <Text
                        key={comparisonIndex}
                        type={"secondary"}
                        className={"text-xs"}
                      >
                        {comparison}
                      </Text>
                    )
                  )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </Card>
  )
}

export { ContractValidationCard }
